import { cn } from 'noddi-ui-common';
import { useLayoutEffect, useRef, useState } from 'react';
import type { StepFlowStep } from './types';

/**
 * Room per step decides whether the names under the segments survive. Measured rather than keyed
 * off step count, because the same nine steps read fine on a wide screen and not at all in a
 * narrow one. Below this the rail is bare segments; the counter header above already names the
 * active step, so nothing is lost.
 */
const MIN_LABELLED_STEP_WIDTH = 72;

export type NoddiStepFlowProps = {
  steps: StepFlowStep[];
  currentStepKey: string;
  completedStepKeys: string[];
  onStepChange: (key: string) => void;
  activeBadgeLabel?: string;
  className?: string;
};

export const NoddiStepFlow = ({
  steps,
  currentStepKey,
  completedStepKeys,
  onStepChange,
  activeBadgeLabel,
  className
}: NoddiStepFlowProps) => {
  const total = steps.length;
  const currentIndex = Math.max(
    0,
    steps.findIndex((s) => s.key === currentStepKey)
  );
  const activeStep = steps[currentIndex];

  const railRef = useRef<HTMLOListElement>(null);
  const [railWidth, setRailWidth] = useState(0);

  // The first read is synchronous and before paint: a ResizeObserver's first callback only lands on
  // the next rendering opportunity, so observing alone paints the narrowest mode once and then
  // jumps — and never corrects at all while the tab is throttled and not painting. The observer
  // only covers later resizes. The rail is w-full, so its width does not depend on the mode this
  // picks and the measurement cannot oscillate.
  useLayoutEffect(() => {
    const rail = railRef.current;
    if (!rail) {
      return;
    }
    setRailWidth(rail.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => setRailWidth(entry?.contentRect.width ?? 0));
    observer.observe(rail);
    return () => observer.disconnect();
  }, []);

  // Unmeasured (railWidth 0) renders as the narrowest mode, so a first paint never overflows.
  const showStepLabels = railWidth / steps.length >= MIN_LABELLED_STEP_WIDTH;

  return (
    <div className={cn('w-full', className)}>
      <div className='mb-4 flex items-end justify-between gap-6'>
        <div className='font-medium tracking-[0.04em] text-muted-foreground tabular-nums'>
          <span className='text-[28px] font-semibold tracking-[-0.01em] text-primary'>
            {String(currentIndex + 1).padStart(2, '0')}
          </span>
          <span className='mx-1 text-primary/20'>/</span>
          <span className='text-base'>{String(total).padStart(2, '0')}</span>
        </div>
        {activeStep && (
          <div key={activeStep.key} className='min-w-0 text-right'>
            <div className='truncate text-[18px] font-semibold tracking-[-0.01em] text-primary motion-safe:animate-in motion-safe:duration-300 motion-safe:fade-in motion-safe:slide-in-from-top-1'>
              {activeStep.label}
            </div>
            {activeStep.description && (
              <div className='text-[13px] text-muted-foreground motion-safe:animate-in motion-safe:delay-75 motion-safe:duration-300 motion-safe:fade-in motion-safe:slide-in-from-top-1'>
                {activeStep.description}
              </div>
            )}
          </div>
        )}
      </div>
      <ol ref={railRef} className='flex w-full gap-1.5'>
        {steps.map((step, index) => {
          const isActive = index === currentIndex;
          // Completed, not "behind": stepping back from the last step leaves the ones ahead answered,
          // and greying them out would strand the user on Back clicks all the way forward again.
          const isComplete = completedStepKeys.includes(step.key) && !isActive;
          const isClickable = isComplete;
          const status: 'complete' | 'active' | 'upcoming' = isActive ? 'active' : isComplete ? 'complete' : 'upcoming';
          return (
            <li key={step.key} className='flex min-w-0 flex-1'>
              <button
                type='button'
                disabled={!isClickable}
                onClick={() => isClickable && onStepChange(step.key)}
                aria-current={isActive ? 'step' : undefined}
                aria-label={`${step.label}${isClickable ? ' (go to step)' : ''}`}
                className='group flex min-w-0 flex-1 flex-col gap-2 bg-transparent p-0 text-left disabled:cursor-default'
              >
                <span
                  className={cn(
                    // Fills take the brand's color and text its ink: the two are one purple for Noddi, but
                    // a brand's color can be too light to read (Carglass yellow).
                    'relative h-1 overflow-hidden rounded-full bg-primary/20 transition-shadow duration-200',
                    status === 'active' && 'ring-3 ring-button-primary/10'
                  )}
                >
                  <span
                    className={cn(
                      'absolute inset-0 origin-left rounded-full transition-transform [transition-duration:450ms] [transition-timing-function:cubic-bezier(0.65,0,0.35,1)]',
                      status === 'upcoming' && 'scale-x-0 bg-button-primary',
                      status === 'complete' && 'scale-x-100 bg-button-primary',
                      status === 'active' &&
                        'scale-x-100 bg-linear-to-r from-button-primary to-card-selected-background'
                    )}
                  />
                </span>
                {showStepLabels && (
                  <span className='flex min-w-0 items-center gap-1.5'>
                    {isActive && activeBadgeLabel && (
                      <span className='shrink-0 rounded-full bg-button-primary px-2 py-0.5 text-[10px] font-semibold tracking-wider text-button-primary-foreground uppercase'>
                        {activeBadgeLabel}
                      </span>
                    )}
                    <span
                      className={cn(
                        'truncate text-xs tracking-[0.01em] transition-colors duration-200',
                        status === 'upcoming' && 'font-medium text-primary/50',
                        status === 'complete' && 'font-medium text-primary',
                        status === 'active' && 'font-semibold text-primary',
                        isClickable && 'group-hover:text-primary'
                      )}
                    >
                      {step.label}
                    </span>
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
